/**
 * API client for the Cloudflare Browser Rendering credential and its cost
 * report (`crm-browser/settings.routes.ts`, `crm-browser/cost.routes.ts`).
 *
 * Its own file beside `mapsSettings.ts` and for the same reasons: one screen,
 * three writes and a read, nothing else in the CRM talks to these routes.
 *
 * ⚠ THE CREDENTIAL IS SHARED. The same token drives the platform's template
 * preview thumbnails and the screenshots the WordPress import takes. Switching
 * it off from the CRM switches those off too. The panel says so out loud; this
 * note is for whoever wonders why a CRM client writes a platform setting.
 */
import { api } from "./api";

/**
 * Where the account id in force came from.
 *
 * `r2` is the one worth a sentence on screen. With no account id of its own the
 * server borrows R2's, because it is the same Cloudflare account - convenient,
 * and invisible until somebody changes R2 and wonders why the browser followed.
 */
export type AccountIdSource = "db" | "env" | "r2" | "none";

/** Which Workers plan the operator says the Cloudflare account is on. */
export type BrowserPlan = "free" | "paid";

/** Why a capture did not produce a picture. The panel phrases each one. */
export type SnapshotFailure =
  | "not_configured"
  | "unsupported_provider"
  | "rate_limited"
  | "unauthorized"
  | "timeout"
  | "upstream_error"
  | "empty_result";

/**
 * The published plan facts, sent by the server rather than repeated here.
 *
 * A screen that hardcoded "ten minutes a day" would keep saying it after
 * Cloudflare changed the allowance, and the first person to notice would be
 * whoever was looking at the panel to find out why captures stopped.
 */
export interface BrowserLimits {
  freeDailyMs: number;
  freeMinCallSpacingMs: number;
  freeConcurrentBrowsers: number;
  paidIncludedMs: number;
  paidOverageUsdPerHour: number;
  paidBaseUsdPerMonth: number;
}

export interface BrowserSettings {
  /** Effective on/off: the switch AND a complete credential. */
  enabled: boolean;
  /** The stored switch, before completeness is taken into account. */
  switchedOn: boolean;
  /**
   * The account id STORED, never the one in force.
   *
   * Empty while the env variable or R2 is supplying it, and that is the point:
   * a form prefilled from the effective value and posted back would quietly
   * freeze a borrowed R2 id into an owned one, so the id keeps following R2
   * only for as long as this field stays empty. `accountIdSource` says where
   * the effective one is coming from instead.
   */
  accountId: string;
  accountIdSource: AccountIdSource;
  /**
   * ⚠ Whether a token exists, NOT the token. It is a real secret - it can drive
   * a browser on somebody else's bill - so it leaves the server the same way the
   * register's contract password does, which is not at all.
   */
  apiTokenSet: boolean;
  tokenSource: "db" | "env" | "none";
  plan: BrowserPlan;
  limits: BrowserLimits;
}

export interface BrowserTestResult {
  ok: boolean;
  /** Present on failure. The panel phrases it through a lookup. */
  reason?: SnapshotFailure;
  /**
   * Browser milliseconds this test cost, when Cloudflare reported them. Null
   * when it did not: the capture still happened and still spent allowance, so
   * an absent duration is not a zero one.
   */
  browserMs?: number | null;
  /** Bytes of PNG that came back. Proof that a browser really ran. */
  bytes?: number;
}

/** Totals for one grouping key - a consumer, or a day. */
export interface BrowserGroupTotal {
  key: string;
  calls: number;
  failed: number;
  browserMs: number;
  /** Of `browserMs`, how much was a flat estimate rather than a measurement. */
  estimatedMs: number;
}

/**
 * Everything that spends browser time (mirrors `BrowserConsumer` in
 * apps/api/src/lib/browser-rendering.ts).
 *
 * A union for the phrasing lookup, but `BrowserGroupTotal.key` stays a plain
 * string: the value comes out of a ledger row, so a consumer added on the
 * server before the CRM learns its name must draw as itself rather than as a
 * blank cell.
 */
export type BrowserConsumer =
  | "brain"
  | "template-thumb"
  | "wp-import"
  | "test"
  | "alert"
  | "platform-news"
  | "monitoring";

export interface BrowserCostReport {
  from: string;
  to: string;
  /** The period held more captures than one report may read; the OLDEST are missing. */
  truncated: boolean;
  calls: number;
  failed: number;
  browserMs: number;
  estimatedMs: number;
  /** How many of `calls` were charged a guess. The panel shows the split. */
  estimatedCalls: number;
  byConsumer: BrowserGroupTotal[];
  byDay: BrowserGroupTotal[];
  failures: Array<{ reason: string; calls: number }>;
  /** The plan the money below was priced on. */
  plan: BrowserPlan;
  /** Included allowance for the plan, in ms. Zero on the free plan, whose wall is daily. */
  includedMs: number;
  overMs: number;
  /** What `overMs` costs, in millionths of a USD. */
  overageMicros: number;
  /**
   * True when the money above is hypothetical - the free plan bills nothing, so
   * `overageMicros` is what this usage WOULD cost on Workers Paid. A useful
   * number and a dangerous one, which is why it never travels without the flag.
   */
  notional: boolean;
  /**
   * Today on its own, because the free plan's wall is a DAILY one and a
   * month-to-date total cannot say whether tonight's runs will go through.
   */
  today: {
    day: string;
    browserMs: number;
    freeDailyMs: number;
    remainingMs: number;
  };
  /**
   * Always true, and stated rather than assumed: no Cloudflare quota, usage or
   * billing endpoint is read anywhere in the stack, so every figure here is our
   * own count of our own calls. A capture made outside this system is invisible.
   */
  selfMetered: boolean;
  /** The Workers Paid subscription, USD a month. Shown as its own line. */
  paidBaseUsdPerMonth: number;
}

export const getBrowserSettings = (): Promise<BrowserSettings> =>
  api.get<BrowserSettings>("/admin/crm/browser/settings");

/**
 * Saves the credential.
 *
 * `accountId` empty CLEARS the stored override and lets the env/R2 fallback
 * take over again - that field only ever shows what is stored, so an emptied box
 * can only mean "stop overriding". `apiToken` omitted means "leave it alone",
 * because that field never shows its contents and blank is its only way of
 * saying nothing changed. The asymmetry is deliberate; see `mapsSettings.ts`,
 * which draws the same line between a public value and a real secret.
 */
export const saveBrowserSettings = (body: {
  accountId: string;
  apiToken?: string;
  enabled: boolean;
  plan: BrowserPlan;
}): Promise<{ ok: boolean }> => api.put("/admin/crm/browser/settings", body);

/**
 * Proves the credential by using it.
 *
 * Answers 200 with `ok: false` on failure rather than throwing: a wrong token is
 * a fact about the form, not a broken server. ⚠ It tests what is SAVED, not what
 * is typed - and it runs a real capture, because Cloudflare publishes no
 * endpoint that validates a token without starting a browser. Both facts are on
 * screen next to the button.
 */
export const testBrowserSettings = (): Promise<BrowserTestResult> =>
  api.post<BrowserTestResult>("/admin/crm/browser/settings/test");

export function getBrowserCost(from: Date, to: Date): Promise<BrowserCostReport> {
  const q = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  return api.get<BrowserCostReport>(`/admin/crm/browser/cost?${q.toString()}`);
}
